import { TrendingUp, Percent, BarChart3, ChevronRight } from 'lucide-react'
import { cn, formatPrice } from '@/lib/utils'

export interface CategoryMetrics {
    categoryId: string
    categoryName: string
    competitorSkusMonitored: number
    pricing: {
        avgSellingPrice: number
    }
    promotions: {
        avgDiscountPercent: number
    }
}

interface CategoryMetricsCardProps {
    category: CategoryMetrics
    isSelected: boolean
    isExpanded: boolean
    onSelect: () => void
    onToggleExpand: () => void
}

export function CategoryMetricsCard({
    category,
    isSelected,
    onSelect,
}: CategoryMetricsCardProps) {
    return (
        <div
            className={cn(
                // Size — parent controls width
                "group/card w-full cursor-pointer",
                // Glass surface
                "rounded-2xl",
                "bg-white/60 dark:bg-white/[0.08]",
                "backdrop-blur-xl backdrop-saturate-[1.4]",
                // Glass edge: top-left highlight + subtle border
                "border border-white/50 dark:border-white/[0.12]",
                "shadow-[0_2px_16px_rgba(0,0,0,0.06),inset_0_1px_0_0_rgba(255,255,255,0.6)]",
                "dark:shadow-[0_2px_16px_rgba(0,0,0,0.3),inset_0_1px_0_0_rgba(255,255,255,0.08)]",
                // Transitions
                "transition-shadow duration-200 ease-out",
                "hover:shadow-[0_8px_30px_rgba(0,0,0,0.1),inset_0_1px_0_0_rgba(255,255,255,0.7)]",
                "hover:border-primary/20",
                // Selected
                isSelected && [
                    "ring-[1.5px] ring-primary/50",
                    "bg-white/70 dark:bg-white/[0.12]",
                    "shadow-[0_8px_30px_rgba(0,0,0,0.1),inset_0_1px_0_0_rgba(255,255,255,0.7)]",
                ]
            )}
            onClick={onSelect}
        >
            <div className="px-4 pt-4 pb-0 flex flex-col justify-between min-h-[120px]">
                {/* Header */}
                <div>
                    <div className="flex items-center gap-2 mb-1">
                        <div className="w-6 h-6 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <BarChart3 className="h-3.5 w-3.5 text-primary" />
                        </div>
                        <h3 className="module-card-title truncate">
                            {category.categoryName}
                        </h3>
                    </div>
                    <p className="module-card-subtitle ml-8">
                        {category.competitorSkusMonitored} competitor SKUs
                    </p>
                </div>

                {/* Metrics */}
                <div className="grid grid-cols-2 gap-3 mt-4">
                    <div className="flex items-center gap-1.5">
                        <TrendingUp className="h-3.5 w-3.5 text-muted-foreground/70 flex-shrink-0" />
                        <div>
                            <span className="module-metric-caption block mb-1">Avg Price</span>
                            <span className="module-metric-value">
                                {category.pricing.avgSellingPrice > 0 ? `₹${formatPrice(category.pricing.avgSellingPrice)}` : '—'}
                            </span>
                        </div>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <Percent className="h-3.5 w-3.5 text-muted-foreground/70 flex-shrink-0" />
                        <div>
                            <span className="module-metric-caption block mb-1">Discount</span>
                            <span className="module-metric-value">
                                {category.promotions.avgDiscountPercent > 0 ? `${formatPrice(category.promotions.avgDiscountPercent)}%` : '—'}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Explore footer */}
            <div className={cn(
                "group/footer relative overflow-hidden flex items-center justify-between px-4 py-2.5 mt-1",
                "border-t border-black/[0.04] dark:border-white/[0.06]",
                "text-xs font-semibold",
                "transition-all duration-300 ease-out",
                "hover:bg-primary/[0.06] hover:border-primary/15",
                isSelected ? "text-primary" : "text-muted-foreground group-hover/card:text-primary/80"
            )}>
                {/* Sliding highlight on hover */}
                <div className="absolute inset-0 -translate-x-full group-hover/footer:translate-x-0 bg-gradient-to-r from-primary/[0.05] via-primary/[0.08] to-transparent transition-transform duration-500 ease-out pointer-events-none" />

                <span className={cn(
                    "relative transition-all duration-300",
                    "group-hover/footer:text-primary group-hover/footer:tracking-[0.08em]",
                    isSelected && "text-primary"
                )}>
                    {isSelected ? 'Viewing products' : 'View products'}
                </span>
                <div className="relative flex items-center gap-0.5">
                    <ChevronRight className={cn(
                        "h-3.5 w-3.5 transition-all duration-300",
                        "group-hover/card:translate-x-0.5",
                        "group-hover/footer:!text-primary group-hover/footer:translate-x-0.5",
                        isSelected && "translate-x-0.5"
                    )} />
                    <ChevronRight className={cn(
                        "h-3.5 w-3.5 -ml-2 transition-all duration-300",
                        "text-transparent",
                        "group-hover/footer:!text-primary/40 group-hover/footer:-ml-1.5 group-hover/footer:translate-x-0.5",
                        isSelected && "text-primary/30 -ml-1.5"
                    )} />
                </div>
            </div>
        </div>
    )
}
